import { authService } from '@/services/authService';
import React, { createContext, useEffect, useState } from 'react';

// Create auth context
const AuthContext = createContext();

/**
 * Auth provider component
 * @param {Object} props - Component props
 * @param {React.ReactNode} props.children - Child components
 * @returns {JSX.Element} AuthProvider component
 */
export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isLoading, setIsLoading] = useState(true);

  // Initialize auth state on app load
  useEffect(() => {
    const initializeAuth = () => {
      try {
        authService.debugCookies();

        const token = authService.getToken();
        const currentUser = authService.getCurrentUser();

        if (token && currentUser) {
          setUser(currentUser);
          setIsAuthenticated(true);
        }
      } catch (error) {
        console.error('Error initializing auth:', error);
        // Clear invalid auth data
        authService.logout();
      } finally {
        setIsLoading(false);
      }
    };

    initializeAuth();
  }, []);

  // Listen for custom events that might be triggered by the API interceptor
  useEffect(() => {
    const handleAuthChange = () => {
      const token = authService.getToken();
      const currentUser = authService.getCurrentUser();

      if (!token || !currentUser) {
        setUser(null);
        setIsAuthenticated(false);
      }
    };

    window.addEventListener('auth-state-changed', handleAuthChange);

    return () => {
      window.removeEventListener('auth-state-changed', handleAuthChange);
    };
  }, []);

  // Background token refresh mechanism
  useEffect(() => {
    if (!isAuthenticated) return;

    const checkAndRefreshToken = async () => {
      try {
        const token = authService.getToken();
        if (!token) return;

        // Decode JWT token to check expiration
        const tokenParts = token.split('.');
        if (tokenParts.length !== 3) return;

        const payload = JSON.parse(atob(tokenParts[1]));
        const currentTime = Math.floor(Date.now() / 1000);
        const timeUntilExpiry = payload.exp - currentTime;

        // If token expires within 30 minutes, refresh it (more conservative)
        if (timeUntilExpiry < 900) {
          await authService.refreshToken();
        }
      } catch (error) {
        console.warn('AuthContext: Error checking token expiry:', error);
      }
    };

    // Check token every 10 minutes (less frequent to avoid conflicts)
    const interval = setInterval(checkAndRefreshToken, 10 * 60 * 1000);

    // Also check immediately
    checkAndRefreshToken();

    return () => clearInterval(interval);
  }, [isAuthenticated]);

  /**
   * Login user
   * @param {Object} credentials - Login credentials
   * @returns {Promise<Object>} Login response
   */
  const login = async credentials => {
    try {
      const response = await authService.login(credentials);

      const currentUser = authService.getCurrentUser();

      setUser(currentUser);
      setIsAuthenticated(true);

      // Dispatch login event to clear React Query cache for fresh data
      window.dispatchEvent(new CustomEvent('auth-login'));

      return response;
    } catch (error) {
      console.error('AuthContext: Login error', error);
      throw error;
    }
  };

  /**
   * Register user
   * @param {Object} userData - User registration data
   * @returns {Promise<Object>} Registration response
   */
  const register = async userData => {
    const response = await authService.register(userData);
    const currentUser = authService.getCurrentUser();

    setUser(currentUser);
    setIsAuthenticated(true);

    // Dispatch login event to clear React Query cache for fresh data
    window.dispatchEvent(new CustomEvent('auth-login'));

    return response;
  };

  /**
   * Logout user
   */
  const logout = () => {
    authService.logout();
    setUser(null);
    setIsAuthenticated(false);

    // Dispatch custom events to notify other components
    window.dispatchEvent(new CustomEvent('auth-state-changed'));
    // Dispatch logout event to clear React Query cache
    window.dispatchEvent(new CustomEvent('auth-logout'));
  };

  /**
   * Update user data
   * @param {Object} userData - Updated user data
   */
  const updateUser = userData => {
    // Normalize user data to ensure consistency (id vs _id)
    const normalizedUser = {
      ...userData,
      id: userData.id || userData._id,
    };

    setUser(normalizedUser);

    // Store user data in cookies (consistent with authService)
    try {
      const expires = new Date();
      expires.setTime(expires.getTime() + 7 * 24 * 60 * 60 * 1000); // 7 days
      document.cookie = `user=${JSON.stringify(normalizedUser)};expires=${expires.toUTCString()};path=/;SameSite=Strict`;
    } catch (error) {
      console.error('AuthContext: Error updating user data in cookies:', error);
    }
  };

  const value = {
    user,
    isAuthenticated,
    isLoading,
    login,
    register,
    logout,
    updateUser,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export default AuthContext;
